import { supabase } from './database/supabase';
import { ErrorType, handleApiError, useError } from '@/shared/contexts/ErrorContext';
import type { FilterOptions } from '@/types/api';
import type { TargetGuest, Guest } from '@/types/models/schema';

// Create a mock addError function for service layer
// In a real implementation, this would be passed from the component using the service
const mockAddError = (error: Parameters<ReturnType<typeof useError>['addError']>[0]) => {
  console.error('API Error:', error);
};

export const targetGuestService = {
  // Get all target guests with filtering
  getAll: async (filters?: FilterOptions): Promise<TargetGuest[] | null> => {
    try {
      let query = supabase.from('target_guests').select('*');
      
      // Apply filters if provided
      if (filters) {
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        
        if (filters.priority) {
          query = query.eq('priority', filters.priority);
        }
        
        if (filters.search) {
          query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,company.ilike.%${filters.search}%`);
        }
        
        // Pagination
        if (filters.limit) {
          query = query.limit(filters.limit);
        }
        
        if (filters.page && filters.limit) {
          const offset = (filters.page - 1) * filters.limit;
          query = query.range(offset, offset + filters.limit - 1);
        }
      }
      
      const { data, error } = await query.order('priority', { ascending: false });
      
      if (error) throw error;
      
      return data as TargetGuest[];
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: 'Failed to fetch target guests'
      });
      return null;
    }
  },
  
  // Get target guest by ID
  getById: async (id: string): Promise<TargetGuest | null> => {
    try {
      const { data, error } = await supabase
        .from('target_guests')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      return data as TargetGuest;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: `Failed to fetch target guest with ID ${id}`
      });
      return null;
    }
  },
  
  // Create target guest
  create: async (data: Omit<TargetGuest, 'id' | 'created_at' | 'updated_at'>): Promise<TargetGuest | null> => {
    try {
      const { data: newTargetGuest, error } = await supabase
        .from('target_guests')
        .insert([{ ...data, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }])
        .select()
        .single();
      
      if (error) throw error;
      
      return newTargetGuest as TargetGuest;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: 'Failed to create target guest'
      });
      return null;
    }
  },
  
  // Update target guest
  update: async (id: string, data: Partial<TargetGuest>): Promise<TargetGuest | null> => {
    try {
      const { data: updatedTargetGuest, error } = await supabase
        .from('target_guests')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return updatedTargetGuest as TargetGuest;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: `Failed to update target guest with ID ${id}`
      });
      return null;
    }
  },
  
  // Delete target guest
  delete: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('target_guests')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return true;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: `Failed to delete target guest with ID ${id}`
      });
      return false;
    }
  },
  
  // Convert target guest to guest
  convertToGuest: async (id: string, additionalData?: Partial<Guest>): Promise<boolean> => {
    try {
      // First get the target guest
      const { data: targetGuest, error: fetchError } = await supabase
        .from('target_guests')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Create a new guest from the target guest data
      const { error: createError } = await supabase
        .from('guests')
        .insert([{
          name: targetGuest.name,
          email: targetGuest.email || '',
          company: targetGuest.company,
          title: targetGuest.title,
          bio: targetGuest.notes,
          status: 'pending',
          location: targetGuest.location,
          ...additionalData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);
      
      if (createError) throw createError;
      
      // Update the target guest status to converted
      const { error: updateError } = await supabase
        .from('target_guests')
        .update({ 
          status: 'converted',
          updated_at: new Date().toISOString() 
        })
        .eq('id', id);
      
      if (updateError) throw updateError;
      
      return true;
    } catch (error) {
      // Use only allowed AppError properties
      mockAddError({
        type: ErrorType.API,
        message: `Failed to convert target guest with ID ${id} to guest`
      });
      return false;
    }
  }
};

export default targetGuestService;
